import { i18n } from '@international/mastodon-i18n';
import {
  PromptGroup,
  PromptGroupKind,
  PromptItem,
} from '../../../../types/candidateStories';
import { stripEmojis } from '../../../../lib/helpers';

export const getPromptGroupByKind = (
  promptGroups: PromptGroup[] | null,
  kind: PromptGroupKind,
): PromptGroup | null => {
  if (!promptGroups) return null;

  return (
    promptGroups.find(group => group.enabled && group.kind === kind) || null
  );
};

export const getGeneralPromptGroup = (
  promptGroups: PromptGroup[] | null,
): PromptGroup | null => {
  return getPromptGroupByKind(promptGroups, PromptGroupKind.GENERAL);
};

export const getCriminalPromptGroup = (
  promptGroups: PromptGroup[] | null,
): PromptGroup | null => {
  return getPromptGroupByKind(promptGroups, PromptGroupKind.CRIMINAL);
};

export const findItemTranslation = (
  promptItem: PromptItem | null,
  lang: string,
) => {
  if (!promptItem) {
    return null;
  }

  return (
    promptItem.translations.find(t => t.language === lang) ||
    promptItem.translations.find(t => lang.startsWith(t.language)) ||
    promptItem.translations.find(t => t.language === 'en')
  );
};

export const getPromptItemById = (
  promptGroups: PromptGroup[],
  promptId: string,
) => {
  const allItems = promptGroups.flatMap(group => group.items);
  return allItems.find(item => item.id === promptId) || null;
};

export const getPromptDetails = (
  promptId: string,
  promptGroups: PromptGroup[] | null,
) => {
  if (!promptGroups || !Array.isArray(promptGroups)) return null;

  const currentLanguage = i18n.getLocale();
  for (const group of promptGroups) {
    const promptItem = group.items?.find(item => item.id === promptId);
    if (promptItem) {
      const translation = findItemTranslation(promptItem, currentLanguage);
      return {
        id: promptId,
        title: translation?.title || '',
      };
    }
  }
  return null;
};

export const formatCustomPromptContent = (
  promptResponses: Record<string, string>,
  promptGroups: PromptGroup[] | null,
) => {
  if (!promptResponses || typeof promptResponses !== 'object') return '';

  const promptArray: Array<{
    prompt: { id: string; title: string };
    response: string;
  }> = [];

  Object.entries(promptResponses).forEach(([promptId, response]) => {
    if (response && typeof response === 'string') {
      const promptDetails = getPromptDetails(promptId, promptGroups);
      if (promptDetails) {
        promptArray.push({
          prompt: promptDetails,
          response: stripEmojis(response),
        });
      }
    }
  });

  return promptArray.length > 0 ? JSON.stringify(promptArray) : '';
};
